import React, { useEffect, useRef, useState } from "react";
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import Paragraph from "@editorjs/paragraph";
import List from "@editorjs/list";
import Quote from "@editorjs/quote";
import CallToAction from "../editorjs/CallToAction";
import ImageBlock from "../editorjs/ImageBlock";
import YoutubeEmbed from "../editorjs/YoutubeEmbed";
import CodeBlock from "../editorjs/CodeBlock";
import ModelBlock from "../editorjs/ModelBlock";
import ClearFormatting from "../editorjs/ClearFormatting";
import Delimiter from "@editorjs/delimiter";
import { loadModelViewer } from "../utils/loadModelViewer";
import MonoButton from "./MonoButton";
import { Link } from "react-router-dom";
import { marked } from "marked";

// —— Hilfsfunktionen ——
const generateSlug = (input) =>
  input.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");



// Markdown → EditorJS Blocks
function markdownToEditorJS(markdownText) {
  const tokens = marked.lexer(markdownText);
  const blocks = [];

  for (const token of tokens) {
    switch (token.type) {
      case "heading":
        blocks.push({
          type: "header",
          data: {
            level: token.depth,
            text: token.text,
          },
        });
        break;

      case "paragraph":
        blocks.push({
          type: "paragraph",
          data: {
            text: token.text,
          },
        });
        break;

      case "list":
        blocks.push({
          type: "list",
          data: {
            style: token.ordered ? "ordered" : "unordered",
            items: token.items.map((i) => i.text),
          },
        });
        break;

      case "blockquote":
        blocks.push({
          type: "quote",
          data: {
            text: token.text,
          },
        });
        break;

      case "code":
        blocks.push({
          type: "code",
          data: {
            content: token.text,
            language: token.lang || "",
          },
        });
        break;

      case "hr":
        blocks.push({ type: "delimiter", data: {} });
        break;

      default:
        break;
    }
  }

  return blocks;
}


function cleanEditorJsBlocks(data = []) {
  const cleanText = (text = "") =>
    text
      .replace(/<[^>]+>/g, "")
      .replace(/[*#]/g, "")
      .replace(/&gt;/g, ">")
      .replace(/&lt;/g, "<")
      .replace(/&amp;/g, "&")
      .replace(/&nbsp;/g, " ")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&mdash;/g, "—")
      .replace(/&ndash;/g, "–");

  return data.map((block) => {
    const cleaned = { ...block };
    if (cleaned.data?.text) cleaned.data.text = cleanText(cleaned.data.text);
    if (Array.isArray(cleaned.data?.items)) {
      cleaned.data.items = cleaned.data.items.map((item) =>
        typeof item === "string"
          ? cleanText(item)
          : { ...item, content: cleanText(item.content) }
      );
    }
    return cleaned;
  });
}

function getPlainAndMarkdown(blocks = []) {
  let plain = [], markdown = [];

  blocks.forEach(({ type, data }) => {
    switch (type) {
      case "header":
        plain.push(data.text);
        markdown.push(`## ${data.text}`);
        break;
      case "paragraph":
        plain.push(data.text);
        markdown.push(data.text);
        break;
      case "quote":
        plain.push(data.text);
        markdown.push(`> ${data.text}`);
        break;
      case "list":
        const items = (data.items || []).map((i) =>
          typeof i === "string" ? i : i.content
        );
        plain.push(items.join("\n"));
        markdown.push(items.map((i) => `- ${i}`).join("\n"));
        break;
      case "code":
        plain.push(data.content);
        markdown.push("```" + (data.language || "") + "\n" + data.content + "\n```");
        break;
      case "delimiter":
        plain.push("---");
        markdown.push("---");
        break;
      case "image":
        const alt = data.alt || data.caption || "Bild";
        const url = data.file?.url || data.url || data.src;
        plain.push(`[Bild: ${alt}] ${url}`);
        markdown.push(url ? `![${alt}](${url})` : "");
        break;
      case "youtube":
        plain.push(`[YouTube: ${data.url}]`);
        markdown.push(`🔗 ${data.url}`);
        break;
      case "cta":
        plain.push(data.content);
        markdown.push(`**${data.content}**`);
        break;
      case "model3d":
        const title = data.title || "3D‑Modell";
        plain.push(title);
        markdown.push(`📦 ${title}`);
        break;
    }
  });

  return {
    plain: plain.join("\n\n"),
    markdown: markdown.join("\n\n"),
  };
}

function download(filename, content, type) {
  const blob = new Blob([content], { type });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// —— Komponente —— //
export default function SmartBlogEditor() {
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [image, setImage] = useState("");
  const [editorData, setEditorData] = useState({ blocks: [] });
  const [availableArticles, setAvailableArticles] = useState([]);
  const ejInstance = useRef(null);
  const slug = generateSlug(title);

  const replaceBlockByType = (type, newData) => {
    const updated = (editorData.blocks || []).map((block) =>
      block.type === type ? { ...block, data: newData } : block
    );
    ejInstance.current?.render({ blocks: updated });
    setEditorData({ blocks: updated });
  };

  useEffect(() => {
    fetch("https://www.denisbitter.de/stage01/api/articles.php")
      .then((res) => res.json())
      .then(setAvailableArticles)
      .catch(console.error);
  }, []);

  useEffect(() => {
    loadModelViewer();
    ejInstance.current = new EditorJS({
      holder: "editorjs",
      autofocus: true,
      tools: {
        header: {
          class: Header,
          inlineToolbar: ["link", "bold", "italic", "clearFormatting"],
          config: { levels: [2, 3, 4], defaultLevel: 2 },
        },
        paragraph: {
          class: Paragraph,
          inlineToolbar: ["link", "bold", "italic", "clearFormatting"],
        },
        list: List,
        quote: Quote,
        cta: CallToAction,
        delimiter: Delimiter,
        image: ImageBlock,
        youtube: YoutubeEmbed,
        code: CodeBlock,
        model3d: { class: ModelBlock, toolbox: ModelBlock.toolbox },
        clearFormatting: ClearFormatting,
      },
      onReady: () => console.log("✅ Editor.js ready"),
    });
    return () => {
      if (ejInstance.current?.destroy) ejInstance.current.destroy();
    };
  }, []);

  const handleSave = async () => {
    const output = await ejInstance.current.save();
    const cleanBlocks = cleanEditorJsBlocks(output.blocks);
    setEditorData({ blocks: cleanBlocks });

    const article = {
      title,
      subtitle,
      date,
      image: image || `https://www.denisbitter.de//images/${slug}.jpg`,
      blocks: cleanBlocks,
    };

    try {
      await fetch("https://www.denisbitter.de/stage01/api/save_articles.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, ...article }),
      });
      alert("✅ Artikel gespeichert!");
    } catch (err) {
      console.error(err);
      alert("❌ Fehler beim Speichern!");
    }
  };

  const loadJson = async (fslug) => {
    const res = await fetch(`https://www.denisbitter.de/stage01/api/articles.php?slug=${fslug}`);
    const json = await res.json();
    const raw = json.blocks || json.content || [];
    const safe = Array.isArray(raw)
      ? raw.map((b) => (b.type === "model" ? { ...b, type: "model3d" } : b))
      : [];
    ejInstance.current.render({ blocks: safe });
    setEditorData({ blocks: safe });
    setTitle(json.title || "");
    setSubtitle(json.subtitle || "");
    setDate(json.date ? json.date.slice(0, 10) : new Date().toISOString().slice(0, 10));
    setImage(json.image || "");
  };

  const { plain, markdown } = getPlainAndMarkdown(editorData.blocks);

  return (
    <div className="max-w-6xl mx-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
      {/* Editor-Seite */}
      <div className="space-y-4">
        <h2 className="text-xs font-bold">✨ Editor.js Blog Editor</h2>

        <select className="border p-2 rounded w-full text-xs" onChange={(e) => loadJson(e.target.value)}>
          <option value="">🗂 Artikel wählen…</option>
          {availableArticles.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>

        <input
          className="w-full border p-2 rounded text-xs"
          value={title}
          placeholder="Titel"
          onChange={(e) => setTitle(e.target.value)}
        />
        <input
          className="w-full border p-2 rounded text-xs"
          value={subtitle}
          placeholder="Untertitel"
          onChange={(e) => setSubtitle(e.target.value)}
        />
        <input
          type="date"
          className="w-full border p-2 rounded text-xs"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <input
          className="w-full border p-2 rounded text-xs"
          value={image}
          placeholder="https://www.denisbitter.de/images/deinbild.jpg"
          onChange={(e) => setImage(e.target.value)}
        />

       <input
  type="file"
  accept=".json,.md"
  className="border p-2 rounded text-xs"
  onChange={(e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = () => {
      const content = reader.result;

      if (file.name.endsWith(".json")) {
        try {
          const json = JSON.parse(content);
          loadJson(json);
        } catch (err) {
          alert("❌ Ungültiges JSON");
        }
      } else if (file.name.endsWith(".md")) {
        // Optional: Lade Markdown als eigenen Block
        const mdBlock = {
          type: "paragraph",
          data: { text: content.replace(/\n/g, "<br>") },
        };
        ejInstance.current?.render({ blocks: [mdBlock] });
        setEditorData({ blocks: [mdBlock] });
        setTitle(file.name.replace(".md", ""));
      } else {
        alert("❗ Nur JSON oder Markdown-Dateien erlaubt.");
      }
    };

    reader.readAsText(file);
  }}
/>

        <div id="editorjs" className="border rounded p-4 bg-white min-h-[300px]" />

        <MonoButton onClick={handleSave} className="mt-4 text-xs">
          💾 Auf Server speichern
        </MonoButton>

        <Link to="/blog">
          <MonoButton className="mt-4 text-xs">BLOG anzeigen</MonoButton>
        </Link>
      </div>

      {/* Vorschau-Seite */}
      <div>
        <h3 className="text-xl font-semibold mb-2">🔍 Vorschau & Optionen</h3>

        {/* JSON Debug */}
        <pre className="bg-white p-4 text-xs rounded border max-h-[250px] overflow-auto mb-4">
          {JSON.stringify(cleanEditorJsBlocks(editorData.blocks), null, 2)}
        </pre>

        {/* Custom JSON-Edit */}
        <textarea
          placeholder='{"type":"paragraph","data":{"text":"Neuer Text"}}'
          className="w-full border p-2 rounded mb-4 text-sm font-mono"
          rows={4}
          onBlur={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              if (parsed.type && parsed.data) replaceBlockByType(parsed.type, parsed.data);
              else alert("❗ Ungültiges JSON-Format.");
            } catch {
              alert("❗ JSON Parsing-Fehler.");
            }
          }}
        />

        {/* Klartext & Markdown */}
        <div className="mt-4 space-y-4">
          <h3 className="text-xs font-semibold">📝 Klartext & Markdown</h3>

          <div>
            <div className="flex gap-2 mb-1">
              <MonoButton onClick={() => navigator.clipboard.writeText(plain)} className="text-xs">
                📋 Klartext kopieren
              </MonoButton>
              <MonoButton onClick={() => download(`${slug}-klartext.txt`, plain, "text/plain")} className="text-xs">
                💾 Klartext herunterladen
              </MonoButton>
            </div>
            <pre className="bg-gray-50 p-3 text-sm rounded max-h-[150px] overflow-auto whitespace-pre-wrap">{plain}</pre>
          </div>

          <div>
            <div className="flex gap-2 mb-1">
              <MonoButton onClick={() => navigator.clipboard.writeText(markdown)} className="text-xs">
                📋 Markdown kopieren
              </MonoButton>
              <MonoButton onClick={() => download(`${slug}-post.md`, markdown, "text/markdown")} className="text-xs">
                💾 Markdown herunterladen
              </MonoButton>
            </div>
            <pre className="bg-gray-50 p-3 text-sm rounded max-h-[150px] overflow-auto whitespace-pre-wrap">{markdown}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}
